import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    let errorMessage: string;
    try {
      const text = await res.text();
      
      // Try to parse as JSON to extract the message field
      try {
        const json = JSON.parse(text);
        errorMessage = json.message || json.error || text || res.statusText;
      } catch {
        // Not JSON, use the text directly
        errorMessage = text || res.statusText;
      }
    } catch (error) {
      // Handle cases where response body cannot be read (DOMException)
      console.error('Failed to read response body:', error);
      errorMessage = res.statusText || `HTTP ${res.status}`;
    }
    throw new Error(errorMessage);
  }
}

// Helper to get auth headers with JWT token if available
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem('auth_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: { timeout?: number }
): Promise<Response> {
  const controller = new AbortController();
  const timeoutMs = options?.timeout || 120000; // Default 2 minutes (120 seconds) for character generation

  console.log(`API Request: ${method} ${url}`, { dataSize: data ? JSON.stringify(data).length : 0 });

  const timeoutId = setTimeout(() => {
    console.log('Request timeout triggered');
    controller.abort();
  }, timeoutMs);

  try {
    console.log('Starting fetch request...');
    const authHeaders = getAuthHeaders();
    const res = await fetch(url, {
      method,
      headers: {
        ...(data ? { "Content-Type": "application/json" } : {}),
        ...authHeaders,
      },
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
      cache: "no-store",
      signal: controller.signal,
    });

    console.log('Fetch completed, status:', res.status);
    clearTimeout(timeoutId);
    await throwIfResNotOk(res);
    console.log('Response validation passed');
    return res;
  } catch (error) {
    console.error('API Request error:', error);
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw error;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join("/") as string;

    try {
      // Use raw fetch with timeout protection but handle 401s specially
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 30000); // 30 second timeout

      const authHeaders = getAuthHeaders();
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        credentials: "include",
        cache: "no-store",
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      // Handle 401s before checking if response is ok
      if (res.status === 401 && unauthorizedBehavior === "returnNull") {
        return null;
      }

      // Now check if response is ok for other status codes
      if (!res.ok) {
        const text = (await res.text()) || res.statusText;
        throw new Error(`${res.status}: ${text}`);
      }

      return await res.json();
    } catch (error) {
      // Handle timeout and other errors
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timed out. Please check your connection and try again.');
        }
        // Handle 401s that might have been thrown as errors
        if (error.message.includes('401') && unauthorizedBehavior === "returnNull") {
          return null;
        }
      }
      throw error;
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
      // Add timeout protection for queries to prevent infinite loading
      gcTime: 30 * 60 * 1000, // 30 minutes cache time
    },
    mutations: {
      retry: false,
      // Mutations inherit timeout from apiRequest (30 seconds default)
    },
  },
});
